import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { ACCOUNT_TIERS, hasActivePass, watermarkStamp } from "@wedding/shared";
import type {
  ActivityDTO,
  DashboardKeepDTO,
  DashboardMomentDTO,
  DashboardPhotoDTO,
  KeepBranding,
  GalleryCountsResponse,
  MomentSummaryDTO,
  PhotoModeration,
  SessionResponse,
  TrendBucket,
} from "@wedding/shared";
import { api, ApiError, downloadFile } from "./api";
import { Engraved, FloralSpray, Icon, Script, SectionHead, Toggle, washFor } from "./ui";

// ── helpers ──
const TONES: Record<string, [string, string]> = {
  rose: ["var(--c-accent-soft)", "var(--c-accent-deep)"],
  amber: ["#f6e6cf", "#b07a2e"],
  blue: ["#dde7ee", "#3a5871"],
  plum: ["#e9dcec", "#8a5a90"],
  sage: ["#dde9dd", "#5f7f57"],
};
function fmtBytes(n: number): string {
  if (n >= 1e9) return (n / 1e9).toFixed(1) + " GB";
  if (n >= 1e6) return (n / 1e6).toFixed(0) + " MB";
  if (n >= 1e3) return (n / 1e3).toFixed(0) + " KB";
  return n + " B";
}
function fmtMoney(cents: number): string {
  return "$" + (cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2));
}
function relTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  return Math.floor(s / 86400) + "d ago";
}
export function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}
function useQr(url: string): string {
  const [d, setD] = useState("");
  useEffect(() => {
    QRCode.toDataURL(url, { width: 480, margin: 1, errorCorrectionLevel: "M" }).then(setD).catch(() => setD(""));
  }, [url]);
  return d;
}

// ══════════════ OVERVIEW ══════════════

const ACT_META: Record<string, { icon: string; tone: string; verb: (m: { count?: number; label?: string }) => string }> = {
  upload: { icon: "upload", tone: "rose", verb: (m) => `${m.count ?? 0} photo${m.count === 1 ? "" : "s"} added` },
  remove: { icon: "trash", tone: "amber", verb: (m) => `${m.count ?? 0} photo${m.count === 1 ? "" : "s"} removed` },
  approve: { icon: "check", tone: "sage", verb: (m) => `${m.count ?? 0} approved` },
  hide: { icon: "eyeoff", tone: "amber", verb: (m) => `${m.count ?? 0} hidden` },
  publish: { icon: "share", tone: "sage", verb: () => "Album published" },
  unpublish: { icon: "eyeoff", tone: "amber", verb: () => "Album unpublished" },
  moment_edit: { icon: "layers", tone: "plum", verb: () => "Moments updated" },
};

export function OverviewSection({ ev, session, go }: { ev: DashboardKeepDTO; session: SessionResponse; go: (s: string) => void }) {
  const [counts, setCounts] = useState<GalleryCountsResponse | null>(null);
  const [moments, setMoments] = useState<MomentSummaryDTO[]>([]);
  const [trend, setTrend] = useState<TrendBucket[]>([]);
  const [feed, setFeed] = useState<ActivityDTO[]>([]);

  useEffect(() => {
    setCounts(null); setMoments([]); setTrend([]); setFeed([]);
    api.galleryCounts(ev.id).then(setCounts).catch(() => {});
    api.moments(ev.id).then((r) => setMoments(r.moments)).catch(() => {});
    api.trend(ev.id).then((r) => setTrend(r.buckets)).catch(() => {});
    api.activity(ev.id).then((r) => setFeed(r.activity)).catch(() => {});
  }, [ev.id]);

  const kpis = [
    { icon: "grid", tone: "rose", val: counts ? counts.totalPhotos.toLocaleString() : "—", lab: "Photos in Keep", sub: "in this Keep" },
    { icon: "shield", tone: "amber", val: counts ? counts.pendingPhotos.toLocaleString() : "—", lab: "Processing", sub: "in the pipeline", warn: true },
    { icon: "face", tone: "plum", val: counts ? counts.faceCount.toLocaleString() : "—", lab: "Faces detected", sub: "across the Keep" },
    { icon: "layers", tone: "sage", val: fmtBytes(session.storageUsed), lab: "Storage (account)", sub: "across all Keeps" },
  ];
  const topMoments = [...moments].filter((m) => m.photoCount > 0).sort((a, b) => b.photoCount - a.photoCount);
  const maxV = Math.max(1, ...trend.map((b) => b.count));
  const trendTotal = trend.reduce((a, b) => a + b.count, 0);

  return (
    <div className="sec overview">
      <SectionHead kicker="Live dashboard" title="The album, at a glance"
        sub="Everything guests are sharing — organized and ready."
        actions={<>
          <button className="abtn ghost" onClick={() => go("settings")}><Icon name="qr" size={16} />Share QR</button>
          <button className="abtn primary" onClick={() => go("moderation")}><Icon name="grid" size={16} />Manage photos</button>
        </>} />

      <div className="kpi-grid">
        {kpis.map((k, i) => (
          <div key={i} className="kpi-card">
            <span className="kpi-ic" style={{ background: TONES[k.tone]![0], color: TONES[k.tone]![1] }}><Icon name={k.icon} size={19} /></span>
            <div className="kpi-val">{k.val}</div>
            <div className="kpi-lab">{k.lab}</div>
            <div className={"kpi-delta" + (k.warn ? " warn" : "")}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="ov-cols">
        <div className="panel">
          <div className="panel-head">
            <div><Engraved size={11.5}>Uploads over time</Engraved><p className="panel-sub">Photos added per hour (last 24 hours)</p></div>
            {trendTotal > 0 && <span className="pill-soft">{trendTotal.toLocaleString()} total</span>}
          </div>
          {trend.length === 0 ? (
            <p className="muted" style={{ padding: "20px 0" }}>No uploads in the last 24 hours yet.</p>
          ) : (
            <div className="bars">
              {trend.map((b, i) => (
                <div className="bar-col" key={i}>
                  <div className="bar-track"><div className="bar-fill" style={{ height: (b.count / maxV) * 100 + "%" }}><span className="bar-tip">{b.count}</span></div></div>
                  <span className="bar-x">{b.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel act-panel">
          <div className="panel-head"><Engraved size={11.5}>Live activity</Engraved><span className="live-tag"><span className="live-dot2" />Live</span></div>
          {feed.length === 0 ? (
            <p className="muted">Activity will appear here as guests upload and you moderate.</p>
          ) : (
            <div className="act-list">
              {feed.map((a) => {
                const meta = ACT_META[a.kind] ?? { icon: "sparkle", tone: "blue", verb: () => a.kind };
                return (
                  <div className="act-item" key={a.id}>
                    <span className="act-ic" style={{ background: TONES[meta.tone]![0], color: TONES[meta.tone]![1] }}><Icon name={meta.icon} size={15} /></span>
                    <div className="act-text">
                      <span>{meta.verb(a.meta)}</span>
                      <span className="act-when"><Icon name="clock" size={11} />{relTime(a.createdAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="panel top-moments">
        <div className="panel-head">
          <div><Engraved size={11.5}>Most-loved moments</Engraved><p className="panel-sub">By photo volume</p></div>
          <button className="link-a" onClick={() => go("moments")}>Manage moments →</button>
        </div>
        {topMoments.length === 0 ? <p className="muted">Moments appear here once photos are processed.</p> : (
          <div className="tm-row">
            {topMoments.slice(0, 6).map((m, i) => (
              <button className="tm-card" key={m.key} onClick={() => go("moments")}>
                <div className="tm-cover" style={m.coverThumbUrl ? undefined : washFor(m.photoCount + i)}>
                  {m.coverThumbUrl ? <img src={m.coverThumbUrl} alt="" /> : <><div className="ptile-grain" /><div className="ptile-vignette" /></>}
                  <span className="tm-rank">{i + 1}</span>
                </div>
                <Engraved size={10}>{m.label}</Engraved>
                <span className="tm-count">{m.photoCount} photos</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════ PHOTOS (moderation) ══════════════

const MFILTERS: [string, string][] = [["pending", "Awaiting review"], ["approved", "Approved"], ["hidden", "Hidden"], ["all", "Everything"]];

export function PhotosSection({ ev }: { ev: DashboardKeepDTO }) {
  const [photos, setPhotos] = useState<DashboardPhotoDTO[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // Default to "pending" when the event holds uploads for review, else "approved".
  const [filter, setFilter] = useState(ev.settings.autoApprove ? "approved" : "pending");
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [dl, setDl] = useState("");

  async function load(reset = false) {
    setLoading(true);
    try {
      const r = await api.listPhotos(ev.id, reset ? undefined : cursor ?? undefined);
      setPhotos((p) => (reset ? r.photos : [...p, ...r.photos]));
      setCursor(r.nextCursor);
      setTotal(r.total);
    } finally { setLoading(false); }
  }
  useEffect(() => { setPhotos([]); setCursor(null); setSel({}); load(true); /* eslint-disable-next-line */ }, [ev.id]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: photos.length, pending: 0, approved: 0, hidden: 0 };
    photos.forEach((p) => { c[p.moderation] = (c[p.moderation] ?? 0) + 1; });
    return c;
  }, [photos]);
  const visible = photos.filter((p) => filter === "all" || p.moderation === filter);
  const selIds = Object.keys(sel).filter((k) => sel[k]);

  async function moderate(ids: string[], moderation: PhotoModeration) {
    for (const id of ids) await api.setModeration(ev.id, id, moderation);
    setPhotos((p) => p.map((x) => (ids.includes(x.photoId) ? { ...x, moderation } : x)));
    setSel({});
  }
  async function delMany(ids: string[]) {
    if (!confirm(`Delete ${ids.length} photo${ids.length === 1 ? "" : "s"} for everyone? This can't be undone.`)) return;
    for (const id of ids) await api.deletePhoto(ev.id, id);
    setPhotos((p) => p.filter((x) => !ids.includes(x.photoId)));
    setTotal((t) => Math.max(0, t - ids.length));
    setSel({});
  }
  async function downloadAll() {
    setDl("Preparing…");
    const urls: string[] = [];
    let cur: string | undefined;
    do {
      const r = await api.listPhotos(ev.id, cur);
      urls.push(...r.photos.filter((p) => p.status === "processed" && p.moderation === "approved").map((p) => p.fullUrl));
      cur = r.nextCursor ?? undefined;
    } while (cur);
    let done = 0;
    for (const u of urls) { setDl(`Downloading ${++done}/${urls.length}…`); await downloadFile(u, u.split("/").pop() || "photo.jpg"); }
    setDl("");
  }

  return (
    <div className="sec">
      <SectionHead kicker="Photo moderation" title="Keep the album lovely"
        sub={ev.settings.autoApprove
          ? "Uploads publish instantly. Hide anything off, or remove it entirely."
          : "Uploads wait here for your approval before guests can see them."}
        actions={<button className="abtn ghost" disabled={!!dl || total === 0} onClick={downloadAll}><Icon name="download2" size={16} />{dl || "Download all"}</button>} />

      <div className="filterbar">
        {MFILTERS.map(([id, label]) => (
          <button key={id} className={"ftab" + (filter === id ? " on" : "")} onClick={() => { setFilter(id); setSel({}); }}>
            {label}<span className="ftab-n">{counts[id] ?? 0}</span>
          </button>
        ))}
      </div>

      {selIds.length > 0 && (
        <div className="bulkbar">
          <span className="bulk-count">{selIds.length} selected</span>
          <div className="bulk-acts">
            <button className="abtn approve" onClick={() => moderate(selIds, "approved")}><Icon name="check" size={15} />Approve</button>
            <button className="abtn hide" onClick={() => moderate(selIds, "hidden")}><Icon name="eyeoff" size={15} />Hide</button>
            <button className="abtn ghost" onClick={() => delMany(selIds)}><Icon name="trash" size={15} />Delete</button>
            <button className="abtn ghost" onClick={() => setSel({})}>Clear</button>
          </div>
        </div>
      )}

      {visible.length === 0 && !loading ? (
        <div className="empty-state">
          <FloralSpray width={170} height={120} />
          <Script size={32} style={{ color: "var(--c-accent)" }}>All caught up</Script>
          <p>No photos in this view.</p>
        </div>
      ) : (
        <div className="mod-grid">
          {visible.map((p) => (
            <div key={p.photoId} className={"mod-card" + (sel[p.photoId] ? " sel" : "")}>
              <div className="mod-photo" onClick={() => setSel((s) => ({ ...s, [p.photoId]: !s[p.photoId] }))}>
                <img src={p.thumbUrl} alt="" loading="lazy" />
                <button className={"mod-check" + (sel[p.photoId] ? " on" : "")} onClick={(e) => { e.stopPropagation(); setSel((s) => ({ ...s, [p.photoId]: !s[p.photoId] })); }}><Icon name="check" size={13} stroke={2.4} /></button>
                {p.moderation !== "approved" && <span className={"mod-badge " + (p.moderation === "hidden" ? "hidden" : "processing")}>{p.moderation}</span>}
                {p.kind === "video" && <span className="mod-badge video" style={{ top: "auto", bottom: 10 }}>video</span>}
              </div>
              <div className="mod-meta">
                <span className="mod-meta-text">
                  <span className="mod-up">Guest upload</span>
                  <span className="mod-sub">{relTime(p.createdAt)}{p.status !== "processed" ? ` · ${p.status}` : ""}</span>
                </span>
              </div>
              <div className="mod-actions">
                <button className="micro approve" title="Approve" onClick={() => moderate([p.photoId], "approved")}><Icon name="check" size={16} /></button>
                <button className="micro hide" title="Hide" onClick={() => moderate([p.photoId], "hidden")}><Icon name="eyeoff" size={16} /></button>
                <button className="micro" title="Delete" onClick={() => delMany([p.photoId])}><Icon name="trash" size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {cursor && (
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button className="abtn ghost" disabled={loading} onClick={() => load(false)}>{loading ? "Loading…" : "Load more"}</button>
        </div>
      )}
    </div>
  );
}

// ══════════════ MOMENTS (read-only, auto-curated) ══════════════

export function MomentsSection({ ev }: { ev: DashboardKeepDTO }) {
  const [moments, setMoments] = useState<DashboardMomentDTO[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  useEffect(() => { setMoments([]); api.listMoments(ev.id).then((r) => setMoments(r.moments)).catch(() => {}); }, [ev.id]);

  function persist(updates: { momentId: string; sortOrder?: number; label?: string | null; hidden?: boolean }[]) {
    api.updateMoments(ev.id, { moments: updates }).catch(() => {});
  }
  function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= moments.length) return;
    const next = [...moments];
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setMoments(next);
    persist(next.map((m, i) => ({ momentId: m.momentId, sortOrder: i })));
  }
  function toggleHide(m: DashboardMomentDTO) {
    setMoments((list) => list.map((x) => (x.momentId === m.momentId ? { ...x, hidden: !x.hidden } : x)));
    persist([{ momentId: m.momentId, hidden: !m.hidden }]);
  }
  function startRename(m: DashboardMomentDTO) { setEditing(m.momentId); setDraft(m.label); }
  function saveRename(m: DashboardMomentDTO) {
    const label = draft.trim();
    setMoments((list) => list.map((x) => (x.momentId === m.momentId ? { ...x, label: label || x.label } : x)));
    persist([{ momentId: m.momentId, label: label || null }]);
    setEditing(null);
  }

  const total = moments.reduce((a, b) => a + b.photoCount, 0);
  const hiddenCount = moments.filter((m) => m.hidden).length;

  return (
    <div className="sec">
      <SectionHead kicker="Album structure" title="Moments & chapters"
        sub="The AI sorts every photo into the night as it unfolded. Reorder the story, rename a chapter, or hide one from guests." />
      <div className="ma-summary">
        <span><strong>{moments.length}</strong> moments</span><span className="dot-sep">·</span>
        <span><strong>{total.toLocaleString()}</strong> photos organized</span><span className="dot-sep">·</span>
        <span><strong>{hiddenCount}</strong> hidden from guests</span>
      </div>
      {moments.length === 0 ? (
        <div className="empty-state"><FloralSpray width={170} height={120} /><p>Moments appear here once photos finish processing.</p></div>
      ) : (
        <div className="ma-list">
          {moments.map((m, i) => (
            <div className={"ma-row" + (m.hidden ? " off" : "")} key={m.momentId}>
              <div className="ma-order">
                <button className="ord-btn" disabled={i === 0} onClick={() => move(i, -1)}><Icon name="arrowup" size={14} /></button>
                <span className="ma-idx">{String(i + 1).padStart(2, "0")}</span>
                <button className="ord-btn" disabled={i === moments.length - 1} onClick={() => move(i, 1)}><Icon name="arrowdown" size={14} /></button>
              </div>
              <div className="ma-cover" style={m.coverThumbUrl ? undefined : washFor(m.photoCount + i)}>
                {m.coverThumbUrl ? <img src={m.coverThumbUrl} alt="" /> : <><div className="ptile-grain" /><div className="ptile-vignette" /></>}
              </div>
              <div className="ma-info">
                {editing === m.momentId ? (
                  <input className="fin" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => saveRename(m)} onKeyDown={(e) => { if (e.key === "Enter") saveRename(m); if (e.key === "Escape") setEditing(null); }}
                    style={{ maxWidth: 280 }} />
                ) : (
                  <>
                    <Engraved size={12} className="ma-name">{m.label}</Engraved>
                    <span className="ma-desc">{m.desc}</span>
                  </>
                )}
              </div>
              <div className="ma-count"><strong>{m.photoCount}</strong><span>photos</span></div>
              <div className="ma-tools">
                <button className="micro" title="Rename" onClick={() => startRename(m)}><Icon name="pencil" size={15} /></button>
                <div className="ma-vis">
                  <Icon name={m.hidden ? "eyeoff" : "eye"} size={15} style={{ color: m.hidden ? "#b07a2e" : "var(--c-ink-soft)" }} />
                  <Toggle on={!m.hidden} onChange={() => toggleHide(m)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════ CONTRIBUTORS (anonymous — honest state) ══════════════

export function ContributorsSection({ ev }: { ev: DashboardKeepDTO }) {
  const [counts, setCounts] = useState<GalleryCountsResponse | null>(null);
  useEffect(() => { setCounts(null); api.galleryCounts(ev.id).then(setCounts).catch(() => {}); }, [ev.id]);
  return (
    <div className="sec">
      <SectionHead kicker="The guest list" title="Who's sharing"
        sub="Guests upload anonymously — no accounts, no friction. Identity-level contributor analytics aren't tracked." />
      <div className="g-stats" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
        {[["Photos shared", counts?.totalPhotos], ["Faces detected", counts?.faceCount], ["Organized", counts?.processedPhotos]].map(([l, n], i) => (
          <div key={i} className="panel" style={{ padding: "16px 18px" }}>
            <strong style={{ display: "block", fontFamily: "var(--font-serif)", fontSize: 28, color: "var(--c-ink)" }}>
              {typeof n === "number" ? n.toLocaleString() : "—"}
            </strong>
            <span className="muted">{l}</span>
          </div>
        ))}
      </div>
      <div className="empty-state">
        <FloralSpray width={180} height={130} />
        <Script size={30} style={{ color: "var(--c-accent)" }}>No accounts to manage</Script>
        <p>Guests contribute by scanning the QR — they never sign up. Per-guest contributor tracking and face-match opt-in management can be added later.</p>
      </div>
    </div>
  );
}

// ══════════════ SETTINGS ══════════════

export function SettingsSection({ ev, session, reload, onDeleted, go }: {
  ev: DashboardKeepDTO; session: SessionResponse; reload: () => void; onDeleted: () => void; go: (s: string) => void;
}) {
  const qr = useQr(ev.guestUrl);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Preview src: a published Keep resolves live (real look). A draft isn't publicly resolvable,
  // so the guest app would fall back to the demo — pass its theme + details so the preview still
  // shows the chosen look. (Ignored the moment a real published Keep resolves.)
  const previewSrc = (() => {
    try {
      const url = new URL(ev.guestUrl);
      if (ev.branding.theme?.preset) {
        url.searchParams.set("theme", ev.branding.theme.preset);
        if (ev.branding.displayName) url.searchParams.set("name", ev.branding.displayName);
        if (ev.branding.eventDate) url.searchParams.set("date", ev.branding.eventDate);
        url.searchParams.set("type", ev.type);
      }
      return url.toString();
    } catch { return ev.guestUrl; }
  })();

  async function togglePublish() {
    setBusy(true);
    try { await api.updateKeep(ev.id, { published: !ev.published }); reload(); }
    catch (e) { alert(e instanceof ApiError ? e.message : "Failed"); } finally { setBusy(false); }
  }
  async function toggleUploads(v: boolean) {
    setBusy(true);
    try { await api.updateKeep(ev.id, { settings: { acceptingUploads: v } }); reload(); }
    catch (e) { alert(e instanceof ApiError ? e.message : "Failed"); } finally { setBusy(false); }
  }
  async function exportAll() {
    let cur: string | undefined; const urls: string[] = [];
    do { const r = await api.listPhotos(ev.id, cur); urls.push(...r.photos.filter((p) => p.status === "processed").map((p) => p.fullUrl)); cur = r.nextCursor ?? undefined; } while (cur);
    for (const u of urls) await downloadFile(u, u.split("/").pop() || "photo.jpg");
  }
  async function remove() {
    if (!confirm(`Delete "${ev.branding.displayName ?? ev.slug}"? Guests lose access and photos are scheduled for deletion.`)) return;
    await api.deleteKeep(ev.id); onDeleted();
  }
  // Download just the QR as a high-res PNG with a transparent background, so the organizer can
  // drop it into their own designs (invitations, signage, etc.).
  async function downloadQrPng() {
    try {
      const url = await QRCode.toDataURL(ev.guestUrl, {
        width: 1024, margin: 1, errorCorrectionLevel: "M",
        color: { dark: "#000000ff", light: "#00000000" },
      });
      const a = document.createElement("a");
      a.href = url; a.download = `${ev.slug}-qr.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* noop — QR generation shouldn't hard-fail the page */ }
  }
  function printPoster() {
    const w = window.open("", "_blank"); if (!w) return;
    const name = ev.branding.displayName ?? ev.slug;
    w.document.write(`<!doctype html><title>${name}</title><body style="margin:0;height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:system-ui;text-align:center"><h1 style="font-size:42px;margin:0 0 6px">${name}</h1><p style="font-size:22px;color:#555;margin:0 0 28px">Scan to add &amp; see the photos</p><img src="${qr}" style="width:340px;height:340px"/><small style="margin-top:22px;color:#888;font-size:16px">${ev.guestUrl}</small><scr${""}ipt>onload=()=>setTimeout(print,200)</scr${""}ipt></body>`);
    w.document.close();
  }

  return (
    <div className="sec">
      <SectionHead kicker="Album settings" title="House rules"
        sub="Control branding, sharing, and how the album behaves." />

      {/* Branding & sharing — sharing tools on the left, live preview + branding on the right. */}
      <div className="panel ba-panel">
        <Engraved size={11.5} className="set-group">Branding &amp; sharing</Engraved>
        <div className="ga-toggles">
          <div className={"ga-tog" + (ev.published ? " on" : "")}>
            <div className="ga-tog-text">
              <span className="ga-tog-title"><Icon name="link" size={15} />Public guest link</span>
              <span className="ga-tog-desc">{ev.published ? "Anyone with the link can view the gallery." : "The link is off — the gallery is private."}</span>
            </div>
            <Toggle on={ev.published} onChange={togglePublish} disabled={busy} />
          </div>
          <div className={"ga-tog" + (ev.settings.acceptingUploads !== false ? " on" : "")}>
            <div className="ga-tog-text">
              <span className="ga-tog-title"><Icon name="upload" size={15} />Accepting guest uploads</span>
              <span className="ga-tog-desc">{ev.settings.acceptingUploads !== false ? "Guests can add new photos." : "Viewing stays on; new uploads are paused."}</span>
            </div>
            <Toggle on={ev.settings.acceptingUploads !== false} onChange={toggleUploads} disabled={busy} />
          </div>
        </div>

        <div className="ba-cols">
          <div className="ba-share">
            <div className="qr-frame">{qr ? <img src={qr} alt="QR" /> : <span className="muted">Generating…</span>}</div>
            <div className="qr-url"><Icon name="link" size={14} />{ev.guestUrl.replace("https://", "")}</div>
            {!ev.published && <p className="err" style={{ textAlign: "center" }}>Turn the public guest link on for the link to work.</p>}
            <div className="qr-btns">
              <button className="abtn ghost sm" disabled={!qr} onClick={downloadQrPng}><Icon name="download2" size={14} />PNG</button>
              <button className="abtn ghost sm" disabled={!qr} onClick={printPoster}><Icon name="download2" size={14} />Poster</button>
              <button className="abtn ghost sm" onClick={() => { navigator.clipboard.writeText(ev.guestUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}><Icon name="link" size={14} />{copied ? "Copied!" : "Copy link"}</button>
              <a className="abtn primary sm" href={ev.guestUrl} target="_blank" rel="noreferrer"><Icon name="share" size={14} />Open</a>
            </div>
          </div>

          <div className="ba-main">
            {/* Live iPhone-sized preview of the guest entry page. Clicking it opens the guest app,
                just like the link (the iframe is non-interactive — the overlay anchor handles clicks). */}
            <a className="ga-phone" href={ev.guestUrl} target="_blank" rel="noreferrer" aria-label="Open guest app">
              <div className="ga-phone-screen">
                <iframe src={previewSrc} title="Guest app preview" tabIndex={-1} scrolling="no" loading="lazy" />
              </div>
              <span className="ga-phone-open"><Icon name="share" size={13} />Open guest app</span>
            </a>
            <BrandingFields ev={ev} reload={reload} />
          </div>
        </div>
      </div>

      <div className="set-cols">
        <div className="set-main">
          <HouseRules ev={ev} reload={reload} />

          <AddOnsPanel ev={ev} session={session} reload={reload} />

          <div className="panel danger">
            <Engraved size={11.5} className="set-group" style={{ color: "var(--c-danger)" }}>Lifecycle</Engraved>
            <div className="set-row">
              <div className="set-text"><span className="set-title">Export the album</span><span className="set-desc">Download every processed photo to your device.</span></div>
              <button className="abtn ghost" onClick={exportAll}><Icon name="download2" size={15} />Export</button>
            </div>
            <div className="set-row">
              <div className="set-text"><span className="set-title">Delete this event</span><span className="set-desc">Removes the album and schedules its photos for deletion.</span></div>
              <button className="abtn danger" onClick={remove}><Icon name="trash" size={15} />Delete</button>
            </div>
          </div>
        </div>

        <div className="set-side">
          <div className="panel event-panel">
            <Engraved size={11} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.24em" }}>Event</Engraved>
            {ev.branding.eventDate && <div className="ev-row"><Icon name="cal" size={15} /><span>{ev.branding.eventDate}</span></div>}
            <div className="ev-row"><Icon name="link" size={15} /><span>{ev.guestUrl.replace("https://", "")}</span></div>
            <div className="ev-row"><Icon name="grid" size={15} /><span>{ev.photoCount.toLocaleString()} photos · {fmtBytes(ev.storageBytes)}</span></div>
            <button className="link-a" style={{ marginTop: 10 }} onClick={() => go("moderation")}>Manage photos →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── house rules (per-event setting toggles) ──
function HouseRules({ ev, reload }: { ev: DashboardKeepDTO; reload: () => void }) {
  const [s, setS] = useState(ev.settings);
  const [busy, setBusy] = useState(false);
  async function set(k: keyof typeof s, v: boolean) {
    const next = { ...s, [k]: v };
    setS(next);
    setBusy(true);
    try { await api.updateKeep(ev.id, { settings: { [k]: v } }); reload(); }
    catch { setS(s); } finally { setBusy(false); }
  }
  const Row = ({ k, title, desc }: { k: keyof typeof s; title: string; desc: string }) => (
    <div className="set-row">
      <div className="set-text"><span className="set-title">{title}</span><span className="set-desc">{desc}</span></div>
      <Toggle on={!!s[k]} disabled={busy} onChange={(v) => set(k, v)} />
    </div>
  );
  return (
    <div className="panel set-panel">
      <Engraved size={11.5} className="set-group">House rules</Engraved>
      <Row k="autoApprove" title="Auto-approve uploads" desc="Publish guest photos instantly. Turn off to review each one before it appears." />
      <Row k="faceMatch" title="Face matching" desc="Let guests find photos of themselves from a selfie." />
      <Row k="watermark" title="Add subtle watermark" desc={`Stamp “${watermarkStamp(ev.branding)}” on downloaded photos.`} />
    </div>
  );
}

// ── branding fields (rendered inside the merged Branding & sharing panel) ──
// Colors come from the chosen theme (not editable here); this edits the text + moments layout.
function BrandingFields({ ev, reload }: { ev: DashboardKeepDTO; reload: () => void }) {
  const b = ev.branding ?? {};
  const t = b.theme ?? {};
  const [displayName, setDisplayName] = useState(b.displayName ?? "");
  const [tagline, setTagline] = useState(b.tagline ?? "");
  const [eventDate, setEventDate] = useState(b.eventDate ?? "");
  const [layout, setLayout] = useState(t.momentsLayout ?? "rows");
  const [err, setErr] = useState(""); const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(""); setOk(false); setBusy(true);
    // Preserve the theme's palette; only the moments layout is editable here.
    const branding: KeepBranding = {
      displayName, tagline, eventDate,
      theme: { ...t, momentsLayout: layout },
    };
    try {
      await api.updateKeep(ev.id, { branding });
      setOk(true); reload();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Save failed"); } finally { setBusy(false); }
  }

  return (
    <div className="ba-form">
      <div className="fld"><label className="fl">Display name</label><input className="fin" value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></div>
      <div className="fld"><label className="fl">Tagline</label><input className="fin" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="A living album of our day" /></div>
      <div className="fld"><label className="fl">Event date</label><input className="fin" value={eventDate} onChange={(e) => setEventDate(e.target.value)} placeholder="June 20 · 2026" /></div>
      <div className="fld"><label className="fl">Moments layout</label>
        <select className="fin" value={layout} onChange={(e) => setLayout(e.target.value as typeof layout)}><option value="rows">Rows</option><option value="grid">Grid</option></select></div>
      {err && <div className="err">{err}</div>}
      {ok && <div className="ok">Saved.</div>}
      <button className="abtn primary" style={{ marginTop: 4, alignSelf: "flex-start" }} disabled={busy} onClick={save}>Save branding</button>
    </div>
  );
}

// ── Keep Pass panel: every Keep is unlimited; publishing needs an active pass ──
function AddOnsPanel({ session }: { ev: DashboardKeepDTO; session: SessionResponse; reload: () => void }) {
  const pass = hasActivePass(session.accountTier);
  const [busy, setBusy] = useState(false);

  async function getPass() {
    setBusy(true);
    try {
      const { url } = await api.subscribe("keeppass");
      window.location.href = url; // Stripe Checkout
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Checkout failed");
      setBusy(false);
    }
  }

  return (
    <div className="panel set-panel">
      <Engraved size={11.5} className="set-group">Your plan</Engraved>
      <div className="set-row">
        <div className="set-text">
          <span className="set-title">{pass ? "Keep Pass — active" : "Keep Pass"}</span>
          <span className="set-desc">
            {pass
              ? `Everything in this Keep is unlimited. ${session.keepCredits} Keep${session.keepCredits === 1 ? "" : "s"} left to create this year.`
              : "Publishing needs a Keep Pass — $49/year for 4 new Keeps, and unused ones roll over."}
          </span>
        </div>
        {pass
          ? <span className="pill-soft">Active</span>
          : <button className="abtn primary" disabled={busy} onClick={getPass}>Get the Keep Pass</button>}
      </div>
      {["Unlimited guests, photos & video", "Forever access & anytime download", "Face matching, AI moments & search"].map((f) => (
        <div className="set-row" key={f}>
          <div className="set-text"><span className="set-title" style={{ fontWeight: 500 }}>{f}</span></div>
          <span className="pill-soft">Included</span>
        </div>
      ))}
    </div>
  );
}
